"use client";

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  Dispatch,
  SetStateAction,
} from "react";
import { GridRowId } from "@mui/x-data-grid-pro";
import { FetchCampaignsV2Response } from "@/api/interfaces/responses/campaigns";
import { Campaign, CampaignType, EmailCadence } from "@/interfaces/campaign";
import getDefaultFilterParams from "@/shared/getDefaultFilterParams";
import { FilterParamsContext } from "@/stories/SearchDrawerComponents/FilterParamsProvider";

interface EditDialogContextType {
  campaign: Campaign | null;
  campaignIdToEdit: GridRowId | null;
  campaignName: string;
  campaignType: CampaignType | null;
  closeEditDialog: () => void;
  editDialogOpen: boolean;
  emailCadence: EmailCadence | null;
  mustIncludeTerms: Record<string, string>;
  openEditDialog: (selectedCampaign: FetchCampaignsV2Response) => void;
  proximity: number | "";
  searchTerms: Record<string, string>;
  setCampaign: Dispatch<SetStateAction<Campaign | null>>;
  setCampaignName: Dispatch<SetStateAction<string>>;
  setEmailCadence: Dispatch<SetStateAction<EmailCadence | null>>;
  setMustIncludeTerms: Dispatch<SetStateAction<Record<string, string>>>;
  setProximity: Dispatch<SetStateAction<number | "">>;
  setSearchTerms: Dispatch<SetStateAction<Record<string, string>>>;
}

const EditDialogContext = createContext<EditDialogContextType | undefined>(
  undefined,
);

export const useEditDialog = () => {
  const context = useContext(EditDialogContext);
  if (!context) {
    throw new Error("useEditDialog must be used within EditDialogProvider");
  }
  return context;
};

interface EditDialogProviderProps {
  children: ReactNode;
}

export function EditDialogProvider({ children }: EditDialogProviderProps) {
  const { setFilterParams } = useContext(FilterParamsContext);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [campaignIdToEdit, setCampaignIdToEdit] = useState<GridRowId | null>(
    null,
  );
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [campaignName, setCampaignName] = useState<string>("");
  const [campaignType, setCampaignType] = useState<CampaignType | null>(null);
  const [emailCadence, setEmailCadence] = useState<EmailCadence | null>(null);
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const [mustIncludeTerms, setMustIncludeTerms] = useState<
    Record<string, string>
  >({});
  const [proximity, setProximity] = useState<number | "">("");

  const openEditDialog = useCallback(
    (selectedCampaign: FetchCampaignsV2Response) => {
      // Extract properties from selectedCampaign
      const campaignId = selectedCampaign.id;
      const campaignName = selectedCampaign.name || "";
      const emailCadence = selectedCampaign.email_cadence;
      const campaignType = selectedCampaign.campaign_type;
      const searchTerms = selectedCampaign.data?.searchTerms;
      const mustIncludeTerms = selectedCampaign.data?.mustIncludeTerms;
      const proximity = selectedCampaign.data?.proximity;
      const filterParams = selectedCampaign.data?.filterParams;

      setCampaignIdToEdit(campaignId);
      setCampaignName(campaignName);
      setEmailCadence(emailCadence || null);
      setCampaignType(campaignType || null);

      // Set filterParams from FilterParamsProvider
      if (filterParams) {
        setFilterParams({
          ...getDefaultFilterParams(),
          ...filterParams,
        });
      } else {
        setFilterParams(getDefaultFilterParams());
      }

      // Convert searchTerms array to Record<string, string>
      const searchTermsRecord: Record<string, string> = {};
      if (searchTerms && Array.isArray(searchTerms)) {
        searchTerms.forEach((term) => {
          searchTermsRecord[term.toLowerCase()] = term;
        });
      }
      setSearchTerms(searchTermsRecord);

      // Convert mustIncludeTerms array to Record<string, string>
      const mustIncludeTermsRecord: Record<string, string> = {};
      if (mustIncludeTerms && Array.isArray(mustIncludeTerms)) {
        mustIncludeTerms.forEach((term) => {
          mustIncludeTermsRecord[term.toLowerCase()] = term;
        });
      }
      setMustIncludeTerms(mustIncludeTermsRecord);

      // Set proximity or default to empty string
      setProximity(proximity !== undefined ? proximity : "");
      setEditDialogOpen(true);
    },
    [setFilterParams],
  );

  const closeEditDialog = useCallback(() => {
    setEditDialogOpen(false);
  }, []);

  return (
    <EditDialogContext.Provider
      value={{
        campaignIdToEdit,
        campaign,
        setCampaign,
        campaignName,
        setCampaignName,
        campaignType,
        emailCadence,
        setEmailCadence,
        searchTerms,
        setSearchTerms,
        mustIncludeTerms,
        setMustIncludeTerms,
        proximity,
        setProximity,
        editDialogOpen,
        closeEditDialog,
        openEditDialog,
      }}
    >
      {children}
    </EditDialogContext.Provider>
  );
}
